<template>
    <div v-if="hasImageLinks || hasTextLinks" class="mb-3 space-y-3 lg:mb-3 varify-inline-filters:lg:mb-4">
        <HorizontalDragScroll v-if="hasImageLinks" class="gap-3 p-1">
            <div class="flex pl-1 lg:mx-auto lg:pl-0 varify-inline-filters:lg:mx-0 varify-inline-filters:lg:pl-1">
                <Link
                    v-for="link in imageLinks"
                    :key="link.url"
                    :href="link.url"
                    class="group flex w-24 shrink-0 snap-start flex-col items-center gap-2 px-1 lg:w-34 lg:px-1.5 varify-inline-filters:lg:w-28 varify-inline-filters:lg:px-1"
                >
                    <BaseImage
                        :src="link.image_url!"
                        :alt="link.label ? stripHtml(link.label) : ''"
                        :width="128"
                        :height="128"
                        loading="lazy"
                        sizes="(min-width: 1024px) 128px, 96px"
                        object-fit="cover"
                        :class="urlIsActive(link.url, page.url) ? 'ring-primary' : 'ring-transparent group-hover:ring-primary'"
                        class="w-24 max-w-full rounded ring-2 ring-offset-2 transition lg:w-32 lg:rounded-xl varify-inline-filters:lg:w-28 varify-inline-filters:lg:rounded"
                    />
                    <span
                        class="line-clamp-3 w-full text-center text-sm leading-tight font-semibold wrap-break-word hyphens-auto transition"
                        v-safe-html="link.label"
                    />
                </Link>
            </div>
        </HorizontalDragScroll>

        <HorizontalDragScroll
            v-if="hasTextLinks"
            class="gap-2 py-1 varify-inline-filters:lg:hidden"
            :class="{ 'lg:hidden': hideOnDesktop, 'max-lg:hidden': hideOnMobile }"
        >
            <Link
                v-for="link in textLinks"
                :key="link.url"
                :href="link.url"
                :class="urlIsActive(link.url, page.url) ? 'border-2 border-primary text-primary' : 'border border-black'"
                class="mt-px shrink-0 snap-start rounded-full px-4 py-1 text-center text-sm whitespace-nowrap transition hover:border-primary hover:text-primary"
                v-safe-html="link.label"
            />
        </HorizontalDragScroll>
    </div>
</template>

<script setup lang="ts">
import BaseImage from '@/components/images/BaseImage.vue';
import HorizontalDragScroll from '@/components/scroll/HorizontalDragScroll.vue';
import { textCategoryLinks } from '@/lib/category/categoryLinks';
import { urlIsActive } from '@/lib/urls';
import type { CategoryLinkObject } from '@/types/generated';
import { stripHtml } from '@/utils/stripHtml';
import { Link, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

const props = defineProps<{
    links: CategoryLinkObject[];
    hideOnDesktop: boolean;
    hideOnMobile: boolean;
}>();

const page = usePage();

const imageLinks = computed((): CategoryLinkObject[] => {
    if (props.hideOnDesktop && props.hideOnMobile) {
        return [];
    }

    // A blank image_url (dead image rewrite placeholders resolve to '') falls back to a text pill.
    return props.links.filter((link: CategoryLinkObject) => !!link.image_url);
});

const textLinks = computed((): CategoryLinkObject[] => {
    if (props.hideOnDesktop && props.hideOnMobile) {
        return [];
    }

    return textCategoryLinks(props.links);
});

const hasImageLinks = computed((): boolean => imageLinks.value.length > 0);
const hasTextLinks = computed((): boolean => textLinks.value.length > 0);
</script>
